import { Spinner, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { ReactNode, useCallback, useMemo } from 'react';
import { Center } from '~/commonComponents/Center';
import { Column } from '~/commonComponents/Column';
import { Pressable } from '~/commonComponents/Pressable';
import { UserProfession } from '~/enums/UserProfession';
import { useAppSelector } from '~/hooks/useAppSelector';
import { IUser } from '~/models';
import { ListItemLegacy } from './ListItemLegacy';
import { ListItem } from './ListItem';

interface IProps {
  EmptyComponent: ReactNode;
  DescriptionComponent: ReactNode;
  isEmpty: boolean;
  isLoading: boolean;
  professionals: IUser[];
  selectedProfessionals: Set<string>;
  selectedProfession?: string;
  onSelectProfessionals: (ids: string[], forceAdd?: boolean) => void;
  hideSelectAllButton?: boolean;
  isNewDesign?: boolean;
  onViewSkills?: (professional: any) => void;
  allSelectableIds?: string[];
  sentProfessionalIds?: Set<string>;
  /** Shift the rows belong to. */
  jobId?: string;
}

export const ProfessionalList = ({
  EmptyComponent,
  DescriptionComponent,
  hideSelectAllButton = false,
  isEmpty,
  isLoading,
  professionals,
  selectedProfession,
  selectedProfessionals,
  onSelectProfessionals,
  isNewDesign = false,
  onViewSkills,
  allSelectableIds,
  sentProfessionalIds = new Set<string>(),
  jobId,
}: IProps) => {
  const allProfessions = useAppSelector((state) => state.user.allProfessions);
  const profession = selectedProfession;

  const getProfession = useCallback(
    (it: IUser) => {
      const isProfessionalHasMultipleProfession = Array.isArray((it as any)?.professions);

      if (isProfessionalHasMultipleProfession) {
        return (it as any).professions?.includes(profession)
          ? allProfessions?.find(({ code }) => profession === code)?.name
          : allProfessions?.find(({ code }) => (it as any).professions[0] === code)?.name;
      }

      return UserProfession.getName((it as any).profession);
    },
    [allProfessions, profession],
  );

  const idsForSelectAll = allSelectableIds ?? professionals.map((it) => it.id);

  const allSelected = useMemo(
    () =>
      idsForSelectAll.length > 0 && idsForSelectAll.every((id) => selectedProfessionals.has(id)),
    [idsForSelectAll, selectedProfessionals],
  );

  const handleSelectAllToggle = useCallback(() => {
    if (allSelected) {
      onSelectProfessionals(idsForSelectAll, false);
    } else {
      onSelectProfessionals(idsForSelectAll, true);
    }
  }, [allSelected, idsForSelectAll, onSelectProfessionals]);

  if (isLoading) {
    return (
      <Center flex={1}>
        <Spinner centered variant="default" size="md" />
      </Center>
    );
  }

  if (isEmpty) {
    return (
      <Column flex={1} flexBasis={0} overflowY="auto" paddingHorizontal={48}>
        {EmptyComponent}
      </Column>
    );
  }

  return (
    <Column
      flex={1}
      flexBasis={0}
      overflowY="auto"
      paddingHorizontal={48}
      style={{
        scrollbarWidth: 'thin',
      }}
    >
      <Column marginBottom={10} marginTop={isNewDesign ? 16 : 0}>
        <Typography
          italic
          color={colors.neutral[500]}
          textAlign={isNewDesign ? 'left' : 'center'}
        >
          {DescriptionComponent}
        </Typography>
        {isNewDesign && !hideSelectAllButton && (
          <Pressable
            onPress={handleSelectAllToggle}
            dataCy="invite-popup-select-all"
            style={{ marginLeft: 'auto', marginTop: 18 }}
          >
            <Typography as="span" bold color={colors.secondary[500]} size="body_large">
              {allSelected ? 'Unselect All' : 'Select All'}
            </Typography>
          </Pressable>
        )}
      </Column>
      {professionals.map((it) => {
        const professionName = getProfession(it);

        if (isNewDesign) {
          const pro = it as any;
          return (
            <ListItem
              key={it.id}
              id={it.id}
              fullName={pro.full_name}
              isAvailable={it.isAvailable || false}
              isFavorite={it.isFavorite}
              isSelected={selectedProfessionals.has(it.id)}
              profileImage={pro.profile_image}
              profession={professionName}
              onItemClick={(id: string) => onSelectProfessionals([id])}
              rating={pro.rating}
              badges={pro.badges ?? []}
              education={pro.education ?? {}}
              professions={pro.professions ?? []}
              jobId={jobId}
              isInviteSent={sentProfessionalIds.has(it.id)}
              onViewSkills={() =>
                onViewSkills?.({
                  fullName: pro.full_name,
                  profession: professionName,
                  professionCode:
                    profession || (Array.isArray(pro.professions) && pro.professions[0]) || '',
                  education: pro.education ?? {},
                })
              }
            />
          );
        }

        return (
          <ListItemLegacy
            key={it.id}
            fullName={it?.full_name}
            id={it.id}
            isAvailable={it.isAvailable || false}
            isFavorite={it.isFavorite}
            isSelected={selectedProfessionals.has(it.id)}
            profession={professionName}
            profileImage={it.profile_image}
            onItemClick={(id: string) => onSelectProfessionals([id])}
            jobId={jobId}
            isInviteSent={sentProfessionalIds.has(it.id)}
          />
        );
      })}
    </Column>
  );
};
